import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowLeft, FloppyDisk, CheckCircle } from "@phosphor-icons/react";

const AdminNewsEdit = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/news").then(({ data }) => {
      const found = data.find((a) => a.id === id);
      setArticle(found || null);
    });
  }, [id]);

  const set = (key, value) => setArticle((a) => ({ ...a, [key]: value }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = { ...article, tags: typeof article.tags === "string" ? article.tags.split(",").map((t) => t.trim()).filter(Boolean) : article.tags };
      const { data } = await api.put(`/admin/news/${id}`, payload);
      setArticle(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally { setSaving(false); }
  };

  if (!article) return <div className="font-mono-tech text-white/40">/ loading...</div>;

  return (
    <div className="cyber" data-testid="admin-news-edit-page">
      <Link to="/admin/news" className="font-mono-tech text-xs text-white tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> All articles</Link>

      <div className="flex items-end justify-between mt-6 mb-8 flex-wrap gap-4">
        <div>
          <div className="term-label">/ edit · {article.slug}</div>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-2 line-clamp-2">{article.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="font-mono-tech text-xs text-cyber-green flex items-center gap-2"><CheckCircle size={14} weight="bold" /> Saved</span>}
          <button onClick={save} disabled={saving} className="btn-neon" data-testid="save-news-btn">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <Section title="/ headline">
            <Field label="Title"><input value={article.title} onChange={(e) => set("title", e.target.value)} className="w-full px-4 py-2.5" data-testid="news-title-input" /></Field>
            <Field label="Excerpt (1–2 sentences)"><textarea value={article.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} rows={3} className="w-full px-4 py-2.5" /></Field>
          </Section>
          <Section title="/ body">
            <Field label="Article body (paragraphs separated by blank lines)">
              <textarea value={article.body || ""} onChange={(e) => set("body", e.target.value)} rows={20} className="w-full px-4 py-2.5" data-testid="news-body-input" />
            </Field>
          </Section>
        </div>

        <div className="lg:col-span-1 space-y-3">
          <Section title="/ meta">
            <Field label="Category"><input value={article.category || ""} onChange={(e) => set("category", e.target.value)} className="w-full px-4 py-2.5" placeholder="Marketing / Payments / Growth ..." /></Field>
            <Field label="Author"><input value={article.author || ""} onChange={(e) => set("author", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Source"><input value={article.source || ""} onChange={(e) => set("source", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Source URL (optional)"><input value={article.source_url || ""} onChange={(e) => set("source_url", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Hero image URL"><input value={article.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Published at (ISO)"><input value={article.published_at || ""} onChange={(e) => set("published_at", e.target.value)} className="w-full px-4 py-2.5" /></Field>
            <Field label="Read time (minutes)"><input type="number" value={article.read_time_min || 0} onChange={(e) => set("read_time_min", parseInt(e.target.value || 0))} className="w-full px-4 py-2.5" /></Field>
            <Field label="Tags (comma separated)"><input value={Array.isArray(article.tags) ? article.tags.join(", ") : (article.tags || "")} onChange={(e) => set("tags", e.target.value)} className="w-full px-4 py-2.5" /></Field>
          </Section>
          <Section title="/ visibility">
            <Field label="Status">
              <select value={article.status} onChange={(e) => set("status", e.target.value)} className="w-full px-4 py-2.5" data-testid="news-status-select">
                <option value="draft">draft</option>
                <option value="published">published</option>
              </select>
            </Field>
            <Field label="Featured">
              <select value={article.featured ? "yes" : "no"} onChange={(e) => set("featured", e.target.value === "yes")} className="w-full px-4 py-2.5">
                <option value="no">no</option>
                <option value="yes">yes</option>
              </select>
            </Field>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="hard-border bg-cyber-surface p-6 space-y-4">
    <div className="term-label">{title}</div>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/60">{label}</label>
    {children}
  </div>
);

export default AdminNewsEdit;
